// src/contexts/AuthContext.js
import React, {
  createContext,
  useState,
  useContext,
  useCallback,
  useEffect,
} from "react";
import wsClient from "../utils/websocket";
import { silentRefresh, setAccessToken, serverLogout } from "../auth/token.js";
import { api } from "../api/axios";
import { can, setPermissions } from "../auth/permissions.js";
import { isPublicPath } from "../auth/publicPaths.js";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isBootstrapping, setIsBootstrapping] = useState(true);
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [userId, setUserId] = useState(null);

  // App-Start: still via Refresh-Cookie ein Access-Token holen
  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Public token pages need no session, a refresh there is a guaranteed 401 on
      // every reload (warehouse TV). Stored tokens stay untouched so a logged-in tab
      // sharing localStorage keeps working.
      if (isPublicPath(window.location.pathname)) {
        setIsBootstrapping(false);
        return;
      }
      const t = await silentRefresh();
      if (cancelled) return;

      if (t) {
        setToken(t);
        setAccessToken(t);
        setIsAuthenticated(true);
        try {
          const { data } = await api.get("/me");
          setPermissions(data.permissions, data.user?.role, data.user);
          setUser(data.user);
          const id =
            data.user?.userId ?? data.user?.id ?? data.user?.sub ?? null;
          if (id != null) setUserId(String(id));
        } catch (error) {
          console.error("[AuthContext] /me error:", error);

          setToken(null);
          setAccessToken(null);
          setIsAuthenticated(false);
          setUser(null);
          setUserId(null);
          await serverLogout();
        }
      } else {
        setToken(null);
        setAccessToken(null);
        setIsAuthenticated(false);
        setUser(null);
        setUserId(null);
      }

      setIsBootstrapping(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (isAuthenticated && token) wsClient.handleAuth(token);
    else wsClient.handleAuth(null);
  }, [isAuthenticated, token]);

  useEffect(
    () => () => {
      wsClient.cleanup();
    },
    [],
  );

  useEffect(() => {
    const handleLogoutRequired = () => {
      setToken(null);
      setAccessToken(null);
      setIsAuthenticated(false);
      setUser(null);
      setUserId(null);
      wsClient.handleAuth(null);
    };

    if (typeof window !== "undefined") {
      window.addEventListener("auth:logout-required", handleLogoutRequired);
      return () => {
        window.removeEventListener(
          "auth:logout-required",
          handleLogoutRequired,
        );
      };
    }
  }, []);

  const login = useCallback(async (newToken) => {
    if (!newToken) return false;

    setToken(newToken);
    setAccessToken(newToken);
    setIsAuthenticated(true);

    try {
      const { data } = await api.get("/me");
      setPermissions(data.permissions, data.user?.role, data.user);
      setUser(data.user);
      const id = data.user?.userId ?? data.user?.id ?? data.user?.sub ?? null;
      if (id != null) setUserId(String(id));
    } catch (error) {
      console.error("[AuthContext] login /me error:", error);
      setToken(null);
      setAccessToken(null);
      setIsAuthenticated(false);
      setUser(null);
      setUserId(null);
      await serverLogout();
    }

    return true;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {
        // ignore
      }
    wsClient.handleAuth(null);
    setToken(null);
    setAccessToken(null);
    setIsAuthenticated(false);
    setUser(null);
    setUserId(null);
    setPermissions([], null);
  }, []);

  const patchUser = useCallback((patch) => {
    setUser((current) => (current ? { ...current, ...patch } : current));
  }, []);

  const value = {
    isAuthenticated,
    isBootstrapping,
    user,
    token,
    userId,
    can,
    login,
    logout,
    patchUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
};

export default AuthContext;
